<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import Auth from 'dashboard/api/auth';
import { setColorTheme } from 'dashboard/helper/themeHelper.js';

const router = useRouter();
const currentUser = useMapGetter('getCurrentUser');
const accountId = useMapGetter('getCurrentAccountId');

// Disponibilidad para Urgentes (Victor 2026-06-07): toggle propio, durable,
// guardado en uiSettings. NO toca el reparto; solo evita que mis chats entren a
// Urgentes cuando estoy Ocupado. Al volver a Disponible reinicia el contador.
const { uiSettings, updateUISettings } = useUISettings();
const urgentPaused = computed(() => uiSettings.value?.urgent_paused === true);
function toggleUrgentAvailability() {
  if (urgentPaused.value) {
    updateUISettings({ urgent_paused: false, urgent_available_since: Date.now() });
  } else {
    updateUISettings({ urgent_paused: true });
  }
}

// inline=true → se ancla DENTRO de la barra (junto a "Resolver"), no flotante.
defineProps({ inline: { type: Boolean, default: false } });

const isOpen = ref(false);
const menuRef = ref(null);

function initials(name) {
  if (!name) return '??';
  return name.split(' ').slice(0, 2).map(s => s[0]?.toUpperCase()).join('');
}

const userInitials = computed(() => initials(currentUser.value?.available_name || currentUser.value?.name));

function toggle() { isOpen.value = !isOpen.value; }
function close() { isOpen.value = false; }

function handleClickOutside(e) {
  if (!isOpen.value) return;
  if (menuRef.value && !menuRef.value.contains(e.target)) close();
}

onMounted(() => document.addEventListener('mousedown', handleClickOutside));
onUnmounted(() => document.removeEventListener('mousedown', handleClickOutside));

// Theme cycle: dark → light → system
const currentTheme = ref(localStorage.getItem('color_scheme') || 'auto');

function getResolvedTheme() {
  if (currentTheme.value === 'dark') return 'dark';
  if (currentTheme.value === 'light') return 'light';
  const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  return isDark ? 'auto-dark' : 'auto-light';
}

function applyTheme(theme) {
  currentTheme.value = theme;
  localStorage.setItem('color_scheme', theme);
  if (theme === 'dark') setColorTheme(true);
  else if (theme === 'light') setColorTheme(false);
  else {
    const isOSDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    setColorTheme(isOSDark);
  }
}

function cycleTheme() {
  const next = currentTheme.value === 'dark' ? 'light'
             : currentTheme.value === 'light' ? 'auto'
             : 'dark';
  applyTheme(next);
}

const themeLabel = computed(() => {
  if (currentTheme.value === 'dark') return 'Oscuro';
  if (currentTheme.value === 'light') return 'Claro';
  return 'Automático';
});

function goProfile() {
  close();
  router.push({ name: 'profile_settings_index', params: { accountId: accountId.value } });
}
function goAppearance() {
  close();
  router.push({ name: 'profile_settings_index', params: { accountId: accountId.value }, hash: '#appearance' });
}
function goNotifications() {
  close();
  router.push({ name: 'profile_settings_index', params: { accountId: accountId.value }, hash: '#notifications' });
}
function logout() {
  close();
  Auth.logout();
}
</script>

<template>
  <div ref="menuRef" class="trp-wrap" :class="{ 'trp-wrap--inline': inline }">
    <button
      class="trp-trigger"
      :class="{ 'trp-trigger-open': isOpen }"
      title="Ajustes"
      @click="toggle"
    >
      <span class="trp-gear">⚙</span>
    </button>

    <div v-if="isOpen" class="trp-dropdown">
      <div class="trp-section-label">Disponibilidad</div>
      <button class="trp-item trp-item-avail" @click="toggleUrgentAvailability">
        <span class="trp-avail-dot" :class="urgentPaused ? 'is-off' : 'is-on'"></span>
        <span class="trp-item-text">{{ urgentPaused ? 'Ocupado · sin Urgentes' : 'Disponible · recibo Urgentes' }}</span>
        <span class="trp-avail-pill" :class="urgentPaused ? 'is-off' : 'is-on'">{{ urgentPaused ? 'Ocupado' : 'Disponible' }}</span>
      </button>

      <div class="trp-divider"></div>

      <div class="trp-section-label">Cuenta</div>
      <button class="trp-item" @click="goProfile">
        <span class="trp-icon">👤</span>
        <span>Mi perfil</span>
      </button>
      <button class="trp-item" @click="goAppearance">
        <span class="trp-icon">🎨</span>
        <span>Apariencia</span>
      </button>
      <button class="trp-item" @click="goNotifications">
        <span class="trp-icon">🔔</span>
        <span>Notificaciones</span>
      </button>

      <div class="trp-section-label trp-section-label-spaced">Tema</div>
      <button class="trp-item trp-item-theme" @click="cycleTheme">
        <span class="trp-icon">🌙</span>
        <span class="trp-item-text">Apariencia (cycle)</span>
        <span class="trp-theme-pill">{{ themeLabel }}</span>
      </button>

      <div class="trp-divider"></div>

      <button class="trp-item trp-item-logout" @click="logout">
        <span class="trp-icon">⎋</span>
        <span>Cerrar sesión</span>
      </button>
    </div>
  </div>
</template>

<style scoped>
/* Fundido junto a "Resolver", arriba der., misma altura · consistente en
   todos los módulos (fixed top-right = misma esquina siempre) · Victor 2026-05-18 */
.trp-wrap {
  position: fixed;
  top: 15px;
  right: 18px;
  z-index: 9999;
  font-family: -apple-system, system-ui, sans-serif;
}
/* Anclado en la barra del header (junto a Resolver): NO flotante, fluye en
   el flex · el dropdown sigue anclando al botón (relative). */
.trp-wrap.trp-wrap--inline {
  position: relative;
  top: auto;
  right: auto;
  z-index: 50;
}

/* Discreto: cuadrado 34px (alto de Resolver), gris translúcido, sin glow
   dorado para no competir con el botón Resolver. */
.trp-trigger {
  width: 34px; height: 34px;
  border-radius: 9px;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.10);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  transition: background 0.15s, border-color 0.15s;
  position: relative;
}
.trp-trigger:hover {
  background: rgba(255,255,255,0.09);
  border-color: rgba(255,255,255,0.18);
}
.trp-trigger:hover .trp-gear { color: rgba(255,255,255,0.9); }
.trp-trigger-open {
  background: rgba(255,255,255,0.09);
  border-color: rgba(255,255,255,0.18);
}
.trp-trigger-open .trp-gear { color: rgba(255,255,255,0.9); }
.trp-gear {
  font-size: 15px;
  color: rgba(255,255,255,0.55);
  transition: color 0.15s;
  line-height: 1;
}

.trp-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 9999;
  width: 290px;
  background: linear-gradient(180deg, rgba(15,15,18,0.98), rgba(8,8,10,0.98));
  border: 1px solid rgba(251,191,36,0.30);
  border-radius: 14px;
  box-shadow: 0 24px 60px rgba(0,0,0,0.65), 0 0 0 1px rgba(251,191,36,0.08), 0 0 40px rgba(251,191,36,0.10);
  padding: 10px;
  overflow: hidden;
  color: #fff;
}
.trp-dropdown::before {
  content: '';
  position: absolute; top: 0; left: 0; right: 0; height: 1px;
  background: linear-gradient(90deg, transparent, rgba(251,191,36,0.60), transparent);
}

.trp-section-label {
  font-size: 9.5px;
  font-weight: 800;
  letter-spacing: 1.2px;
  color: rgba(251,191,36,0.85);
  text-transform: uppercase;
  padding: 6px 12px 4px;
}
.trp-section-label-spaced { margin-top: 8px; }

.trp-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 9px 12px;
  background: transparent;
  border: none;
  color: rgba(255,255,255,0.85);
  font-size: 12.5px;
  font-weight: 600;
  font-family: inherit;
  text-align: left;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
}
.trp-item:hover {
  background: rgba(251,191,36,0.08);
  color: #fff;
}

.trp-icon {
  font-size: 14px;
  width: 18px;
  display: flex;
  justify-content: center;
}

.trp-item-text { flex: 1; }
.trp-theme-pill {
  font-size: 10px;
  font-weight: 900;
  padding: 2px 8px;
  border-radius: 999px;
  background: linear-gradient(135deg, #fbbf24, #d97706);
  color: #18181b;
  letter-spacing: 0.4px;
}

.trp-divider {
  height: 1px;
  background: rgba(255,255,255,0.08);
  margin: 8px 4px;
}

/* Disponibilidad para Urgentes (Victor 2026-06-07) */
.trp-item-avail { gap: 11px; }
.trp-avail-dot {
  width: 9px; height: 9px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-left: 4px;
}
.trp-avail-dot.is-on { background: #34c759; box-shadow: 0 0 8px rgba(52,199,89,0.7); }
.trp-avail-dot.is-off { background: rgba(255,255,255,0.30); }
.trp-avail-pill {
  font-size: 10px;
  font-weight: 900;
  padding: 2px 9px;
  border-radius: 999px;
  letter-spacing: 0.3px;
}
.trp-avail-pill.is-on { background: rgba(52,199,89,0.16); color: #5fd07e; }
.trp-avail-pill.is-off { background: rgba(255,255,255,0.08); color: rgba(255,255,255,0.55); }

.trp-item-logout {
  color: #f87171;
}
.trp-item-logout:hover {
  background: rgba(239,68,68,0.12);
  color: #fca5a5;
}
</style>
